import { call, put, takeLatest, takeEvery, all } from 'redux-saga/effects';
import {
  PLAYLIST_FETCH_REQUEST, PLAYLIST_CREATE_REQUEST, PLAYLIST_UPDATE_REQUEST, PLAYLIST_DELETE_REQUEST,
  type PlaylistFetchRequestPayload, type PlaylistCreateRequestPayload, type PlaylistUpdateRequestPayload, type PlaylistDeleteRequestPayload,
} from './playlistTypes';
import { playlistFetchSuccess, playlistFetchFailure, playlistAdd, playlistUpdate, playlistDelete } from './playlistActions';
import { createPlaylistApi, fetchPlaylistsApi, updatePlaylistApi, deletePlaylistApi, type PlaylistApiItem, type PlaylistDeleteApiResponse } from './playlistApi';
import type { Playlist } from '../../../components/display/data/types';

const mapPlaylistItem = (item: PlaylistApiItem): Playlist => ({
  id: item.id,
  name: item.name,
  orientation: item.orientation === 'PORTRAIT' ? 'Portrait' : 'Landscape',
  mediaEntries: (item.mediaEntries ?? []).map((e) => ({ mediaId: e.mediaId, sequenceNo: e.sequenceNo, displayTime: String(e.displayTime) })),
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
});

/** Real backend: GET /display/playlist?locationId= — displayGet wraps the raw body as { data: rawBody },
 *  and rawBody is itself { data: [...] }, hence the double unwrap (res.data.data). */
function* fetchPlaylistsSaga(action: { type: string; payload: PlaylistFetchRequestPayload }): Generator<any, void, any> {
  try {
    const res: { data: { data: PlaylistApiItem[] } } = yield call(fetchPlaylistsApi, action.payload.locationId);
    yield put(playlistFetchSuccess((res.data?.data ?? []).map(mapPlaylistItem)));
  } catch (err: any) {
    yield put(playlistFetchFailure(err?.message || 'Failed to load playlists'));
  }
}

/** mediaIds arrives in play order — sequenceNo is derived from that order (1-based). */
const toMediaEntries = (mediaIds: string[]) => mediaIds.map((mediaId, idx) => ({ mediaId, sequenceNo: idx + 1 }));

/** Real backend: POST /display/playlist */
function* createPlaylistSaga(action: { type: string; payload: PlaylistCreateRequestPayload }): Generator<any, void, any> {
  const { locationId, name, orientation, mediaIds, deferred } = action.payload;
  try {
    const apiOrientation = orientation === 'Portrait' ? 'PORTRAIT' : 'LANDSCAPE';
    const res: { data: { data: PlaylistApiItem } } = yield call(createPlaylistApi, { locationId, name, orientation: apiOrientation, mediaEntries: toMediaEntries(mediaIds) });
    const playlist = mapPlaylistItem(res.data.data);
    yield put(playlistAdd(playlist));
    deferred.resolve(playlist);
  } catch (err: any) {
    deferred.reject(new Error(err?.message || 'Failed to create playlist'));
  }
}

/** Real backend: POST /display/playlist/{id} */
function* updatePlaylistSaga(action: { type: string; payload: PlaylistUpdateRequestPayload }): Generator<any, void, any> {
  const { id, locationId, name, orientation, mediaIds, deferred } = action.payload;
  try {
    const apiOrientation = orientation === 'Portrait' ? 'PORTRAIT' : 'LANDSCAPE';
    const res: { data: { data: PlaylistApiItem } } = yield call(updatePlaylistApi, id, { locationId, name, orientation: apiOrientation, mediaEntries: toMediaEntries(mediaIds) });
    const playlist = mapPlaylistItem(res.data.data);
    yield put(playlistUpdate(playlist));
    deferred.resolve(playlist);
  } catch (err: any) {
    deferred.reject(new Error(err?.message || 'Failed to update playlist'));
  }
}

/** Real backend: DELETE /display/playlist/{id}?locationId= */
function* deletePlaylistSaga(action: { type: string; payload: PlaylistDeleteRequestPayload }): Generator<any, void, any> {
  const { id, locationId, deferred } = action.payload;
  try {
    const res: { data: { data: PlaylistDeleteApiResponse } } = yield call(deletePlaylistApi, id, locationId);
    if (res.data?.data?.deleted === false) throw new Error('Playlist was not deleted');
    yield put(playlistDelete(id));
    deferred.resolve();
  } catch (err: any) {
    deferred.reject(new Error(err?.message || 'Failed to delete playlist'));
  }
}

export function* playlistSaga(): Generator<any, void, any> {
  yield all([
    takeLatest(PLAYLIST_FETCH_REQUEST, fetchPlaylistsSaga),
    // takeEvery — create/update/delete are independent user actions and shouldn't cancel one another.
    takeEvery(PLAYLIST_CREATE_REQUEST, createPlaylistSaga),
    takeEvery(PLAYLIST_UPDATE_REQUEST, updatePlaylistSaga),
    takeEvery(PLAYLIST_DELETE_REQUEST, deletePlaylistSaga),
  ]);
}
